// @flow
export const PLATFORMS = {
  YOUTUBE: 'YOUTUBE',
  TIKTOK: 'TIKTOK',
  INSTAGRAM: 'INSTAGRAM',
  FACEBOOK: 'FACEBOOK',
};

export function detectPlatform(url: string): string | null {
  if (!url) return null;

  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return PLATFORMS.YOUTUBE;
  }
  if (lowerUrl.includes('tiktok.com')) {
    return PLATFORMS.TIKTOK;
  }
  if (lowerUrl.includes('instagram.com')) {
    return PLATFORMS.INSTAGRAM;
  }
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.watch')) {
    return PLATFORMS.FACEBOOK;
  }

  return null;
}

export function isEmbedUrl(url: string, platform: string): boolean {
  if (!url) return false;

  const lowerUrl = url.toLowerCase();

  switch (platform) {
    case PLATFORMS.YOUTUBE:
      return lowerUrl.includes('/embed/');

    case PLATFORMS.TIKTOK:
      return lowerUrl.includes('tiktok.com/embed/');

    case PLATFORMS.INSTAGRAM:
      return lowerUrl.includes('/embed');

    case PLATFORMS.FACEBOOK:
      return lowerUrl.includes('facebook.com/plugins/video.php');

    default:
      return false;
  }
}

function extractYouTubeId(url: string): string | null {
  try {
    const urlObj = new URL(url);

    // youtube.com/watch?v=ID
    if (urlObj.hostname.includes('youtube.com')) {
      const videoId = urlObj.searchParams.get('v');
      if (videoId) return videoId;

      // youtube.com/embed/ID
      if (urlObj.pathname.includes('/embed/')) {
        const parts = urlObj.pathname.split('/');
        const idIndex = parts.indexOf('embed') + 1;
        if (parts[idIndex]) return parts[idIndex];
      }

      // youtube.com/shorts/ID
      if (urlObj.pathname.includes('/shorts/')) {
        const parts = urlObj.pathname.split('/');
        const idIndex = parts.indexOf('shorts') + 1;
        if (parts[idIndex]) return parts[idIndex];
      }
    }

    // youtu.be/ID
    if (urlObj.hostname.includes('youtu.be')) {
      const id = urlObj.pathname.split('/')[1];
      if (id) return id;
    }

    return null;
  } catch (e) {
    return null;
  }
}

function extractTikTokId(url: string): string | null {
  try {
    const urlObj = new URL(url);

    // tiktok.com/embed/ID
    if (urlObj.pathname.includes('/embed/')) {
      const parts = urlObj.pathname.split('/');
      const idIndex = parts.indexOf('embed') + 1;
      if (parts[idIndex]) return parts[idIndex];
    }

    // tiktok.com/@usuario/video/ID
    if (urlObj.pathname.includes('/video/')) {
      const parts = urlObj.pathname.split('/');
      const idIndex = parts.indexOf('video') + 1;
      if (parts[idIndex]) return parts[idIndex];
    }

    return null;
  } catch (e) {
    return null;
  }
}

function extractInstagramId(url: string): string | null {
  try {
    const urlObj = new URL(url);

    // instagram.com/p/ID | /reel/ID | /tv/ID
    const pathParts = urlObj.pathname.split('/').filter(p => p);

    if (pathParts.includes('p')) {
      const idIndex = pathParts.indexOf('p') + 1;
      if (pathParts[idIndex]) return pathParts[idIndex];
    }

    if (pathParts.includes('reel')) {
      const idIndex = pathParts.indexOf('reel') + 1;
      if (pathParts[idIndex]) return pathParts[idIndex];
    }

    if (pathParts.includes('tv')) {
      const idIndex = pathParts.indexOf('tv') + 1;
      if (pathParts[idIndex]) return pathParts[idIndex];
    }

    return null;
  } catch (e) {
    return null;
  }
}

export function convertToEmbedUrl(
  url: string,
  platform: string
): string | null {
  if (!url || !platform) return null;

  if (isEmbedUrl(url, platform)) {
    return url;
  }

  try {
    switch (platform) {
      case PLATFORMS.YOUTUBE: {
        const videoId = extractYouTubeId(url);
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
        return null;
      }

      case PLATFORMS.TIKTOK: {
        const videoId = extractTikTokId(url);
        if (videoId) {
          return `https://www.tiktok.com/embed/${videoId}`;
        }
        return null;
      }

      case PLATFORMS.INSTAGRAM: {
        const postId = extractInstagramId(url);
        if (postId) {
          return `https://www.instagram.com/p/${postId}/embed`;
        }
        return null;
      }

      case PLATFORMS.FACEBOOK: {
        const encodedUrl = encodeURIComponent(url);
        return `https://www.facebook.com/plugins/video.php?height=314&href=${encodedUrl}&show_text=false&width=560&t=0`;
      }

      default:
        return null;
    }
  } catch (e) {
    return null;
  }
}

export function validateVideoUrl(
  url: string
): {
  isValid: boolean,
  platform: string | null,
  embedUrl: string | null,
  error?: string,
} {
  if (!url || !url.trim()) {
    return {
      isValid: false,
      platform: null,
      embedUrl: null,
      error: 'URL vacía',
    };
  }

  try {
    new URL(url);
  } catch (e) {
    return {
      isValid: false,
      platform: null,
      embedUrl: null,
      error: 'URL inválida',
    };
  }

  const platform = detectPlatform(url);
  if (!platform) {
    return {
      isValid: false,
      platform: null,
      embedUrl: null,
      error: 'Plataforma no soportada',
    };
  }

  const embedUrl = convertToEmbedUrl(url, platform);
  if (!embedUrl) {
    return {
      isValid: false,
      platform,
      embedUrl: null,
      error: 'No se pudo extraer el ID del video',
    };
  }

  return {
    isValid: true,
    platform,
    embedUrl,
  };
}

export function extractIframeSrc(htmlSnippet: string): string | null {
  if (!htmlSnippet || !htmlSnippet.includes('<iframe')) {
    return null;
  }

  try {
    const srcMatch = htmlSnippet.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1];
    }
    return null;
  } catch (e) {
    return null;
  }
}

export function extractInstagramUrl(htmlSnippet: string): string | null {
  if (!htmlSnippet || !htmlSnippet.includes('instagram')) {
    return null;
  }

  try {
    const permalinkMatch = htmlSnippet.match(
      /data-instgrm-permalink=["']([^"']+)["']/i
    );
    if (permalinkMatch && permalinkMatch[1]) {
      return permalinkMatch[1].replace(/&amp;/g, '&');
    }

    const hrefMatch = htmlSnippet.match(
      /href=["'](https:\/\/www\.instagram\.com\/[^"']+)["']/i
    );
    if (hrefMatch && hrefMatch[1]) {
      return hrefMatch[1].replace(/&amp;/g, '&');
    }

    return null;
  } catch (e) {
    return null;
  }
}

export function extractTikTokUrl(htmlSnippet: string): string | null {
  if (!htmlSnippet || !htmlSnippet.includes('tiktok')) {
    return null;
  }

  try {
    const citeMatch = htmlSnippet.match(/cite=["']([^"']+)["']/i);
    if (citeMatch && citeMatch[1]) {
      return citeMatch[1];
    }
    const videoIdMatch = htmlSnippet.match(/data-video-id=["'](\d+)["']/i);
    if (videoIdMatch && videoIdMatch[1]) {
      const hrefMatch = htmlSnippet.match(
        /href=["'](https:\/\/www\.tiktok\.com\/[^"']+)["']/i
      );
      if (hrefMatch && hrefMatch[1]) {
        return hrefMatch[1];
      }
    }

    return null;
  } catch (e) {
    return null;
  }
}

export function normalizeVideoInput(input: string): string {
  if (!input) return input;

  const trimmedInput = input.trim();

  if (trimmedInput.includes('<iframe')) {
    const src = extractIframeSrc(trimmedInput);
    if (src) return src;
  }

  if (
    trimmedInput.includes('<blockquote') &&
    trimmedInput.includes('instagram')
  ) {
    const url = extractInstagramUrl(trimmedInput);
    if (url) return url;
  }

  if (trimmedInput.includes('<blockquote') && trimmedInput.includes('tiktok')) {
    const url = extractTikTokUrl(trimmedInput);
    if (url) return url;
  }

  return trimmedInput;
}

export function isFacebookVertical(url: string): boolean {
  if (!url) return true;

  try {
    if (url.includes('facebook.com/plugins/video.php')) {
      const urlObj = new URL(url);
      const width = urlObj.searchParams.get('width');
      const height = urlObj.searchParams.get('height');

      if (width && height) {
        const widthNum = parseInt(width, 10);
        const heightNum = parseInt(height, 10);

        return widthNum <= heightNum;
      }

      const hrefParam = urlObj.searchParams.get('href');
      if (hrefParam) {
        const decodedHref = decodeURIComponent(hrefParam);
        if (decodedHref.toLowerCase().includes('/reel/')) {
          return true;
        }
      }
    }

    if (url.toLowerCase().includes('/reel/')) {
      return true;
    }

    return true;
  } catch (e) {
    return true;
  }
}
